import {byClass, byClassAll} from "./dom-utils";
import {listenAll} from "./events";

// CSS class selectors
const CLASSES = {
    GALLERY: 'media-gallery',
    GALLERY_MAIN: 'media-gallery__main',
    GALLERY_PREVIEWS: 'media-gallery__previews',
    PROGRESS_CONTAINER: 'media-gallery__main__progress',
    PROGRESS_BAR: 'media-gallery__main__progress-bar',
    MEDIA_MAIN: 'media--main',
    MEDIA_PREVIEW: 'media--preview',
    ACTIVE: 'g-active'
}

// Default configuration
const DEFAULTS = {
    TRANSITION_MS: 800,
    AUTOPLAY_INTERVAL_MS: 5000
}

// CSS Generators
const CSS = {
    mainContainer: (transitionMs) => `
        position: relative;
        width: 100%;
        transition: all ${transitionMs}ms ease-in-out;
    `,

    absoluteLayer: (opacity, transitionMs) => `
        position: absolute;
        opacity: ${opacity};
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        object-fit: contain;
        transition: opacity ${transitionMs}ms ease-out;
    `,

    progressTransition: (durationMs) => `
        transition: width ${durationMs}ms linear;
    `,
    
    progressWidth: (percent) => `
        width: ${percent}%;
    `
}

function createProgressBar() {
    const progressContainer = document.createElement('div')
    progressContainer.className = CLASSES.PROGRESS_CONTAINER
    const progressBar = document.createElement('div')
    progressBar.className = CLASSES.PROGRESS_BAR
    progressContainer.appendChild(progressBar)
    return progressContainer
}

function initGal(gal) {
    const main = byClass(CLASSES.GALLERY_MAIN, gal)
    const previewBlock = byClass(CLASSES.GALLERY_PREVIEWS, gal)
    const previews = Array.from(previewBlock.children)
    let autoplayIntervalId = null
    let isTransitioning = false
    let isManualNavigation = false
    
    // Configuration from datasets with defaults
    const transitionMs = parseInt(gal.dataset.transitionMs) || DEFAULTS.TRANSITION_MS
    const autoplayIntervalMs = parseInt(gal.dataset.autoplayInterval) || DEFAULTS.AUTOPLAY_INTERVAL_MS
    
    // Create progress bar once at initialization
    const progressContainer = createProgressBar()
    main.appendChild(progressContainer)
    const progressBar = progressContainer.firstChild
    
    function setMainTo(target) {
        if (isTransitioning) return
        isTransitioning = true

        console.log('setting main to ', target)
        main.style.cssText = CSS.mainContainer(transitionMs)

        const newNode = target.cloneNode()
        newNode.innerHTML = target.innerHTML
        newNode.style.cssText = CSS.absoluteLayer(0, transitionMs)
        newNode.classList.add(CLASSES.MEDIA_MAIN)
        newNode.classList.remove(CLASSES.MEDIA_PREVIEW)
        
        const oldNode = main.querySelector(`.${CLASSES.MEDIA_MAIN}`)
        if (oldNode) {
            oldNode.style.cssText = CSS.absoluteLayer(1, transitionMs)
        }

        const sourceTags = Array.from(newNode.getElementsByTagName('source'))
        const imgTags = Array.from(newNode.getElementsByTagName('img'))
        const vidTags = Array.from(newNode.getElementsByTagName('video'))
        const vid = vidTags && vidTags[0]
        const sources = sourceTags.concat(imgTags, vidTags)
        const n1 = Date.now()
        
        if (vid) {
            vid.setAttribute('loading', 'eager')
            vid.load()
            vid.classList.remove(CLASSES.MEDIA_PREVIEW)
        }
        
        main.appendChild(newNode)

        var _sload = false
        function onSourceLoaded() {
            if (_sload) { return }
            _sload = true
            
            console.log('loaded in', (Date.now() - n1))
            newNode.style.opacity = '1'
            if (oldNode) {
                oldNode.style.opacity = '0'
            }
            
            main.style.cssText = CSS.mainContainer(transitionMs)
            
            setTimeout(() => {
                onTransitionEnd()
            }, transitionMs)
        }

        function onTransitionEnd() {
            console.log('transition ended in', (Date.now() - n1))
            if (oldNode) {
                oldNode.remove()
            }
            newNode.style.cssText = ''
            main.style.cssText = ''
            
            if (!isManualNavigation) {
                startAutoplay()
            }
            isTransitioning = false
        }

        // when any of the sources loads
        if (vid && vid.readyState > 0) {
            requestAnimationFrame(onSourceLoaded)
            return
        }
        listenAll('load', sources, onSourceLoaded)
        listenAll('loadeddata', vidTags, onSourceLoaded)
        listenAll('canplay', vidTags, onSourceLoaded)
    }

    function switchTo(clickEvt) {
        if (isTransitioning) return
        
        const target = clickEvt.currentTarget
        isManualNavigation = true
        stopAutoplay()
        setMainTo(target)
        previews.forEach(p => p.classList.toggle(CLASSES.ACTIVE, target === p))
    }

    function startAutoplay() {
        if (autoplayIntervalId) return
        
        progressBar.style.cssText = CSS.progressTransition(autoplayIntervalMs)
        setTimeout(() => {
            progressBar.style.cssText = CSS.progressTransition(autoplayIntervalMs) + CSS.progressWidth(100)
        }, 50)
        
        autoplayIntervalId = setInterval(() => {
            const activeIndex = previews.findIndex(p => p.classList.contains(CLASSES.ACTIVE))
            const nextIndex = (activeIndex + 1) % previews.length
            setMainTo(previews[nextIndex])
            previews.forEach(p => p.classList.toggle(CLASSES.ACTIVE, previews[nextIndex] === p))
            
            // Reset progress bar
            progressBar.style.cssText = CSS.progressWidth(0)
            requestAnimationFrame(() => {
                progressBar.style.cssText = CSS.progressTransition(autoplayIntervalMs) + CSS.progressWidth(100)
            })
        }, autoplayIntervalMs)
    }

    function stopAutoplay() {
        if (autoplayIntervalId) {
            clearInterval(autoplayIntervalId)
            autoplayIntervalId = null
            // Reset and pause progress bar
            progressBar.style.cssText = CSS.progressWidth(0)
        }
    }

    // Initialize first image without triggering autoplay
    isManualNavigation = true
    const firstImage = previews[0]
    setMainTo(firstImage)
    previews.forEach(p => p.classList.toggle(CLASSES.ACTIVE, firstImage === p))
    isManualNavigation = false  // Reset after initialization

    // Event listeners
    gal.addEventListener('mouseenter', stopAutoplay)
    gal.addEventListener('mouseleave', () => {
        isManualNavigation = false  // Reset flag when mouse leaves
        startAutoplay()
    })
    listenAll('click', previews, switchTo)
}

export function initGalleries() {
    console.debug('initGalleries')
    const galleries = byClassAll(CLASSES.GALLERY)
    Array.from(galleries).forEach(initGal)
}